import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import SectionHeading from '../SectionHeading/SectionHeading';
import { useLanguage } from '../../contexts/LanguageContext';
import { sendContactEmail, validateContactForm, initializeEmailJS } from '../../utils/emailService';
import { successToastConfig, errorToastConfig } from '../../utils/toastConfig';

const Contact = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    msg: ''
  });

  // Initialize EmailJS when component mounts
  useEffect(() => {
    initializeEmailJS();
  }, []);

  // Handler for input field changes
  const handleInputChange = event => {
    const { name, value } = event.target;
    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: value,
    }));
    
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prevErrors => ({
        ...prevErrors,
        [name]: ''
      }));
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setFormErrors({});

    // Validate form data
    const validation = validateContactForm(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      setLoading(false);
      toast.error(t('contact.form.validationError'), errorToastConfig);
      return;
    }

    try {
      // Send email using our email service
      await sendContactEmail(formData);
      
      // Show success toast
      toast.success(t('contact.form.successMessage'), successToastConfig);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        msg: ''
      });
      
    } catch (error) {
      console.error('Email sending error:', error);
      toast.error(t('contact.form.errorMessage'), errorToastConfig);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="st-shape-wrap" id="contact">
      <div className="st-shape1">
        <img src="shape/contact-shape1.svg" alt="shape1" />
      </div>
      <div className="st-shape2">
        <img src="shape/contact-shape2.svg" alt="shape2" />
      </div>
      <div className="st-height-b60 st-height-lg-b40" />
      <SectionHeading
        title={t('contact.title')}
        subTitle={t('contact.subtitle')}
      />
      <div className="container">
        <div className="row">
          <div className="col-lg-10 offset-lg-1">
            <div id="st-alert" />
            <form
              onSubmit={onSubmit}
              className="row st-contact-form st-type1"
              method="post"
              id="contact-form"
            >
              <input type="hidden" name="from_name" value="TerminTeiler Medical" />
              <input type="hidden" name="replyto" value="support@terminteiler.com" />
              <div className="col-lg-6">
                <div className="st-form-field st-style1">
                  <label>{t('contact.form.fullName')}</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    placeholder={t('contact.form.fullNamePlaceholder')}
                    required
                    onChange={handleInputChange}
                    value={formData.name}
                    className={formErrors.name ? 'error' : ''}
                  />
                  {formErrors.name && <div className="error-message">{formErrors.name}</div>}
                </div>
              </div>
              {/* .col */}
              <div className="col-lg-6">
                <div className="st-form-field st-style1">
                  <label>{t('contact.form.email')}</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder={t('contact.form.emailPlaceholder')}
                    required
                    onChange={handleInputChange}
                    value={formData.email}
                    className={formErrors.email ? 'error' : ''}
                  />
                  {formErrors.email && <div className="error-message">{formErrors.email}</div>}
                </div>
              </div>
              {/* .col */}
              <div className="col-lg-6">
                <div className="st-form-field st-style1">
                  <label>{t('contact.form.subject')}</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    placeholder={t('contact.form.subjectPlaceholder')}
                    required
                    onChange={handleInputChange}
                    value={formData.subject}
                    className={formErrors.subject ? 'error' : ''}
                  />
                  {formErrors.subject && <div className="error-message">{formErrors.subject}</div>}
                </div>
              </div>
              {/* .col */}
              <div className="col-lg-6">
                <div className="st-form-field st-style1">
                  <label>{t('contact.form.phone')}</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder={t('contact.form.phonePlaceholder')}
                    required
                    onChange={handleInputChange}
                    value={formData.phone}
                    className={formErrors.phone ? 'error' : ''}
                  />
                  {formErrors.phone && <div className="error-message">{formErrors.phone}</div>}
                </div>
              </div>
              {/* .col */}
              <div className="col-lg-12">
                <div className="st-form-field st-style1">
                  <label>{t('contact.form.message')}</label>
                  <textarea
                    cols={30}
                    rows={10}
                    id="msg"
                    name="msg"
                    placeholder={t('contact.form.messagePlaceholder')}
                    required
                    onChange={handleInputChange}
                    value={formData.msg}
                    className={formErrors.msg ? 'error' : ''}
                  />
                  {formErrors.msg && <div className="error-message">{formErrors.msg}</div>}
                </div>
              </div>
              {/* .col */}
              <div className="col-lg-12">
                <div className="text-center">
                  <div className="st-height-b10 st-height-lg-b10" />
                  <button
                    className="st-btn st-style1 st-color1 st-size-medium"
                    type="submit"
                    id="submit"
                    name="submit"
                    disabled={loading}
                  >
                    {loading ? t('contact.form.sendingButton') : t('contact.form.sendButton')}
                  </button>
                </div>
              </div>
              {/* .col */}
            </form>
          </div>
          {/* .col */}
        </div>
      </div>
      <div className="st-height-b60 st-height-lg-b40" />
    </section>
  );
};

export default Contact;